if (!customElements.get('complete-the-set')) {
  customElements.define(
    'complete-the-set',
    class CompleteTheSet extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        const configEl = this.querySelector('[data-complete-the-set-config]');
        if (!configEl) return;

        this.config = JSON.parse(configEl.textContent);
        this.selected = new Map();
        this.items = new Map();
        this.errorEl = this.querySelector('[data-error]');
        this.cta = this.querySelector('[data-add-set]');
        this.ctaLabel = this.querySelector('[data-cta-label]');
        this.progressBadge = this.querySelector('[data-progress-badge]');

        this.querySelectorAll('[data-complete-the-set-item]').forEach((element) => {
          const jsonEl = element.querySelector('[data-item-json]');
          if (!jsonEl) return;

          const data = JSON.parse(jsonEl.textContent);
          const productId = String(data.id);
          const initialVariant = this.getInitialVariant(data);

          this.items.set(productId, {
            ...data,
            element,
            variantSelect: element.querySelector('[data-variant-select]'),
            toggle: element.querySelector('[data-item-toggle]'),
            toggleLabel: element.querySelector('[data-toggle-label]'),
            priceEl: element.querySelector('[data-price-current]'),
            compareEl: element.querySelector('[data-price-compare]'),
            imageEl: element.querySelector('.complete-the-set__image'),
            currentVariantTitle: element.querySelector('[data-current-variant-title]'),
          });

          if (data.isCurrent && initialVariant) {
            this.selected.set(productId, initialVariant.id);
          }

          this.bindItem(productId);
        });

        this.sortItemsByPrice();
        this.cta?.addEventListener('click', this.onAddSet.bind(this));
        this.bindMainProductVariant();
        this.updateUI();
      }

      disconnectedCallback() {
        this.unsubscribeVariantChange?.();
      }

      sortItemsByPrice() {
        const scroller = this.querySelector('[data-item-grid]');
        if (!scroller) return;

        const cards = Array.from(scroller.querySelectorAll('[data-complete-the-set-item]'));
        cards.sort((first, second) => {
          const itemA = this.items.get(String(first.dataset.productId));
          const itemB = this.items.get(String(second.dataset.productId));
          const priceA = Number(this.getSelectedVariant(itemA)?.price || 0);
          const priceB = Number(this.getSelectedVariant(itemB)?.price || 0);
          return priceB - priceA;
        });
        cards.forEach((card) => scroller.appendChild(card));
      }

      getInitialVariant(data) {
        if (data.isCurrent) {
          const configured = data.variants.find((variant) => variant.id === this.config.currentVariantId);
          if (configured) return configured;
        }

        return data.variants.find((variant) => variant.available) || data.variants[0] || null;
      }

      bindItem(productId) {
        const item = this.items.get(productId);
        if (!item) return;

        item.variantSelect?.addEventListener('change', () => {
          const variant = this.getVariant(item, Number(item.variantSelect.value));
          if (!variant || !variant.available) {
            this.restoreAvailableSelection(item);
            return;
          }

          this.updateItemDisplay(item, variant);

          if (this.selected.has(productId)) {
            this.selected.set(productId, variant.id);
            this.updateUI();
          }
        });

        item.toggle?.addEventListener('click', () => {
          if (item.isCurrent) return;
          this.toggleItem(productId);
        });

        item.element.addEventListener('click', (event) => {
          if (item.isCurrent) return;
          if (event.target.closest('a, select, button, label')) return;
          this.toggleItem(productId);
        });
      }

      bindMainProductVariant() {
        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event.data?.variant;
            if (!variant?.id) return;

            const current = this.items.get(String(this.config.currentProductId));
            if (!current) return;

            const matchesProduct =
              Number(variant.product_id) === Number(this.config.currentProductId) ||
              current.variants.some((itemVariant) => itemVariant.id === variant.id);

            if (!matchesProduct) return;
            this.updateCurrentVariant(variant.id, variant);
          });
        }

        const mainProductInfo = document.querySelector(
          `product-info[data-product-id="${this.config.currentProductId}"]`
        );
        mainProductInfo?.querySelectorAll('form[data-type="add-to-cart-form"] input[name="id"]').forEach((input) => {
          input.addEventListener('change', () => {
            const variantId = Number(input.value);
            if (variantId) this.updateCurrentVariant(variantId);
          });
        });
      }

      updateCurrentVariant(variantId, liveVariant) {
        const item = this.items.get(String(this.config.currentProductId));
        if (!item) return;

        const variant = this.getVariant(item, Number(variantId));
        if (!variant) return;

        if (liveVariant) {
          if (typeof liveVariant.price === 'number') variant.price = liveVariant.price;
          if (liveVariant.compare_at_price !== undefined) variant.compareAtPrice = liveVariant.compare_at_price;
          if (typeof liveVariant.available === 'boolean') variant.available = liveVariant.available;
          if (liveVariant.title) variant.title = liveVariant.title;
        }

        this.selected.set(String(item.id), variant.id);
        this.updateItemDisplay(item, variant);
        item.element.classList.toggle('is-sold-out', !variant.available);
        this.updateUI();
      }

      toggleItem(productId) {
        const item = this.items.get(productId);
        if (!item || item.isCurrent) return;

        const variant = this.getSelectedVariant(item);
        if (!variant?.available) return;

        if (this.selected.has(productId)) {
          this.selected.delete(productId);
        } else {
          this.selected.set(productId, variant.id);
        }

        this.clearError();
        this.updateUI();
      }

      getSelectedVariant(item) {
        if (!item) return null;
        if (item.variantSelect) {
          return this.getVariant(item, Number(item.variantSelect.value));
        }
        return item.variants.find((variant) => variant.available) || item.variants[0] || null;
      }

      getVariant(item, variantId) {
        return item?.variants.find((variant) => variant.id === Number(variantId)) || null;
      }

      restoreAvailableSelection(item) {
        const available = item.variants.find((variant) => variant.available);
        if (!available || !item.variantSelect) return;
        item.variantSelect.value = String(available.id);
        this.updateItemDisplay(item, available);
      }

      updateItemDisplay(item, variant) {
        if (item.priceEl) item.priceEl.textContent = this.formatMoney(variant.price);

        if (item.compareEl) {
          const showCompare = Boolean(variant.compareAtPrice) && variant.compareAtPrice > variant.price;
          item.compareEl.textContent = showCompare ? this.formatMoney(variant.compareAtPrice) : '';
          item.compareEl.toggleAttribute('hidden', !showCompare);
        }

        if (item.currentVariantTitle) {
          item.currentVariantTitle.textContent = variant.title;
        }

        const imageUrl = variant.image || item.featuredImage;
        if (item.imageEl && imageUrl) {
          item.imageEl.src = imageUrl;
          item.imageEl.srcset = '';
        }
      }

      getSelectedLineItems() {
        const lines = [];

        this.selected.forEach((variantId, productId) => {
          const item = this.items.get(String(productId));
          const variant = this.getVariant(item, variantId);
          if (!item || !variant) return;

          lines.push({
            productId: item.id,
            isCurrent: Boolean(item.isCurrent),
            variant,
          });
        });

        return lines;
      }

      updateUI() {
        const lines = this.getSelectedLineItems();
        const count = lines.length;
        const minItems = this.config.minItems;
        const unlocked = count >= minItems;
        const originalTotal = lines.reduce((sum, line) => sum + Number(line.variant.price || 0), 0);
        const discountedTotal = unlocked
          ? Math.round(originalTotal * (1 - this.config.discountPercent / 100))
          : originalTotal;
        const saved = originalTotal - discountedTotal;
        const currentLine = lines.find((line) => line.isCurrent);
        const currentAvailable = Boolean(currentLine?.variant?.available);

        this.items.forEach((item, productId) => {
          const isSelected = this.selected.has(productId);
          const variant = this.getSelectedVariant(item);
          const available = Boolean(variant?.available);

          item.element.classList.toggle('is-selected', isSelected);

          if (item.isCurrent) {
            if (item.toggleLabel) item.toggleLabel.textContent = this.config.strings.included;
            return;
          }

          item.toggle?.setAttribute('aria-pressed', String(isSelected));
          item.toggle?.toggleAttribute('disabled', !available);

          if (item.toggleLabel) {
            if (!available) {
              item.toggleLabel.textContent = this.config.strings.soldOut;
              item.toggle?.setAttribute('aria-label', `${item.title}: ${this.config.strings.soldOut}`);
            } else {
              item.toggleLabel.textContent = isSelected ? this.config.strings.remove : this.config.strings.add;
              item.toggle?.setAttribute(
                'aria-label',
                `${isSelected ? this.config.strings.remove : this.config.strings.add} ${item.title}`
              );
            }
          }
        });

        if (this.progressBadge) {
          this.progressBadge.textContent = unlocked
            ? this.interpolate(this.config.strings.progressUnlocked, { count })
            : this.interpolate(this.config.strings.progressCount, {
                count,
                min: minItems,
              });
        }

        const totalLabel = this.querySelector('[data-total-label]');
        const originalEl = this.querySelector('[data-total-original]');
        const discountedEl = this.querySelector('[data-total-discounted]');
        const savedRow = this.querySelector('[data-saved-row]');
        const savedEl = this.querySelector('[data-total-saved]');

        if (totalLabel) {
          totalLabel.textContent = this.interpolate(this.config.strings.setTotal, { count });
        }
        if (originalEl) {
          originalEl.textContent = this.formatMoney(originalTotal);
          originalEl.toggleAttribute('hidden', !unlocked);
        }
        if (discountedEl) discountedEl.textContent = this.formatMoney(discountedTotal);
        if (savedEl) {
          savedEl.innerHTML = this.interpolate(this.config.strings.youSave, {
            amount: `<span class="complete-the-set__saved-amount">${this.formatMoney(saved)}</span>`,
            percent: this.config.discountPercent,
          });
        }

        savedRow?.toggleAttribute('hidden', !unlocked || !this.config.showSavings || saved <= 0);

        if (this.ctaLabel) {
          const ctaCount = unlocked ? count : minItems;
          this.ctaLabel.textContent = this.interpolate(
            ctaCount === 1 ? this.config.strings.addToBagSingular : this.config.strings.addToBag,
            { count: ctaCount }
          );
        }

        const canSubmit = unlocked && currentAvailable && lines.every((line) => line.variant.available);
        this.cta?.toggleAttribute('disabled', !canSubmit);
      }

      openCart(cart) {
        if (cart?.open) {
          cart.open();
          return;
        }

        window.location = window.routes.cart_url;
      }

      async onAddSet() {
        if (this.cta?.hasAttribute('disabled') || this.cta?.classList.contains('loading')) return;

        const lines = this.getSelectedLineItems();
        const unavailable = lines.find((line) => !line.variant.available);

        if (lines.length < this.config.minItems) return;

        if (unavailable) {
          this.showError(
            unavailable.isCurrent ? this.config.strings.unavailable : this.config.strings.error
          );
          this.updateUI();
          return;
        }

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.clearError();
        this.setLoading(true);

        const items = lines.map((line) => ({
          id: line.variant.id,
          quantity: 1,
          properties: {
            _bundle_source: 'complete-the-set',
            _bundle_collection: this.config.collectionHandle,
          },
        }));

        const body = {
          items,
          sections_url: window.location.pathname,
        };

        if (cart?.getSectionsToRender) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
        }

        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = JSON.stringify(body);

          const response = await fetch(`${routes.cart_add_url}`, config);
          const parsed = await response.json();

          if (parsed.status) {
            this.showError(parsed.description || parsed.message || this.config.strings.error);
            return;
          }

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'complete-the-set',
              cartData: parsed,
            });
          }

          if (parsed.sections) {
            cart.renderContents(parsed);
            cart.classList.remove('is-empty');
          } else {
            this.openCart(cart);
          }
        } catch (error) {
          console.error(error);
          this.showError(this.config.strings.error);
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        if (!this.cta) return;

        this.cta.classList.toggle('loading', isLoading);
        this.cta.toggleAttribute('aria-disabled', isLoading);
        this.cta.setAttribute('aria-busy', String(isLoading));
        this.cta.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.hidden = false;
        this.errorEl.textContent = message;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      interpolate(template, vars) {
        return String(template || '').replace(/\[(\w+)\]/g, (_, key) =>
          vars[key] !== undefined && vars[key] !== null ? String(vars[key]) : ''
        );
      }

      formatMoney(cents) {
        const value = Number(cents || 0);
        const format = this.config.moneyFormat || '£{{amount}}';
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const match = format.match(placeholderRegex);
        const placeholder = match ? match[1] : 'amount';

        const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
          if (Number.isNaN(number) || number == null) return '0';
          const parts = (number / 100.0).toFixed(precision).split('.');
          const pounds = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          return precision ? `${pounds}${decimal}${parts[1]}` : pounds;
        };

        let formatted;
        switch (placeholder) {
          case 'amount_no_decimals':
            formatted = formatWithDelimiters(value, 0);
            break;
          case 'amount_with_comma_separator':
            formatted = formatWithDelimiters(value, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            formatted = formatWithDelimiters(value, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            formatted = formatWithDelimiters(value, 2, "'", '.');
            break;
          case 'amount_no_decimals_with_space_separator':
            formatted = formatWithDelimiters(value, 0, ' ');
            break;
          case 'amount_with_space_separator':
            formatted = formatWithDelimiters(value, 2, ' ', ',');
            break;
          case 'amount_with_period_and_space_separator':
            formatted = formatWithDelimiters(value, 2, ' ', '.');
            break;
          default:
            formatted = formatWithDelimiters(value, 2);
        }

        return format.replace(placeholderRegex, formatted);
      }
    }
  );
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-complete-the-set-jump]');
  if (!trigger) return;

  const target = document.getElementById('CompleteTheSet');
  if (!target) return;

  event.preventDefault();
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});
